import type { NamatAssessment } from './namatAssessment';

/**
 * The manual "remind them" button (#97): who counts as "has not filled the
 * questionnaire", and the toast that summarises what the server did.
 *
 * «لم يملأ» is one rule everywhere, web and mobile: the assessment status is
 * not 'valid' — never done, or expired. A row whose assessment failed to load
 * is not counted; the server re-checks every id anyway.
 */

/** The server refuses more than this many ids in one call. */
export const REMIND_MAX_IDS = 500;

export function needsQuestionnaire(assessment: Pick<NamatAssessment, 'status'> | null | undefined): boolean {
  return !!assessment && assessment.status !== 'valid';
}

/** The ids of the rows that need the questionnaire, deduplicated, in order. */
export function idsNeedingQuestionnaire<T>(
  rows: T[],
  idOf: (row: T) => string,
  assessmentOf: (row: T) => Pick<NamatAssessment, 'status'> | null | undefined,
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const id = idOf(row);
    if (id && !seen.has(id) && needsQuestionnaire(assessmentOf(row))) seen.add(id);
  }
  return [...seen];
}

/** What the `remindQuestionnaire` callable returns: counts only. */
export interface RemindQuestionnaireResult {
  mode: 'live' | 'dry_run';
  sent: number;
  deferred: number;
  /** Dry run only: how many would have been reminded. */
  wouldSend: number;
  skippedCap: number;
  skippedValid: number;
  skippedNotEligible: number;
  failed: number;
}

export function confirmReminderText(count: number, isArabic: boolean): string {
  return isArabic ? `إرسال تذكير لـ ${count} مستفيداً؟` : `Send a reminder to ${count} beneficiaries?`;
}

/** The toast text, one clause per non-zero count. */
export function reminderSummary(result: RemindQuestionnaireResult, isArabic: boolean): string {
  if (result.mode === 'dry_run') {
    return isArabic
      ? `التذكير متوقف حالياً — لم يُرسل شيء، وسيصل لـ ${result.wouldSend} عند التفعيل`
      : `Reminders are switched off — nothing was sent; ${result.wouldSend} will be reminded once enabled`;
  }
  const parts: string[] = [];
  if (isArabic) {
    if (result.sent) parts.push(`أُرسل التذكير لـ ${result.sent}`);
    if (result.deferred) parts.push(`${result.deferred} سيصلهم بعد انتهاء ساعات الهدوء`);
    if (result.skippedCap) parts.push(`${result.skippedCap} ذُكّروا هذا الأسبوع فلم يُرسل لهم`);
    if (result.failed) parts.push(`${result.failed} لم يصلهم (الإشعارات مغلقة أو لا جهاز)`);
    return parts.length ? parts.join('، ') : 'لم يُرسل أي تذكير';
  }
  if (result.sent) parts.push(`Reminder sent to ${result.sent}`);
  if (result.deferred) parts.push(`${result.deferred} will get it after quiet hours`);
  if (result.skippedCap) parts.push(`${result.skippedCap} skipped — already reminded this week`);
  if (result.failed) parts.push(`${result.failed} not delivered (notifications off or no device)`);
  return parts.length ? parts.join('; ') : 'No reminders were sent';
}
